import {
  DefaultAlertDialog,
  DefaultModalDialog,
  InputField,
  LabeledText,
  SelectField,
  Text,
} from '~/common/components/defaultUIComponents';
import {
  StampType,
  Timestamp,
  TimestampDate,
} from '~/common/entities/Employees/EmployeeTypes';
import WorkingInfoApi from '~/common/api/zeiterfassungServerApi/WorkingInfoApi';
import { Box, Flex, Grid, Tooltip, useDisclosure } from '@chakra-ui/react';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { formatName } from '~/common/utils/employeeUtils';
import { useDisabledColor, useIsDisabled } from './utils';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import useTranslate from '~/common/utils/translate';
import * as dateFns from 'date-fns';
import * as yup from 'yup';
import React from 'react';

type Props = {
  employee: Employee;
  ts: Timestamp;
  date: TimestampDate;
};

export const TimestampBox: React.FC<Props> = ({ employee, ts, date }) => {
  const t = useTranslate();
  const disabled = useIsDisabled(date);
  const color = useDisabledColor(disabled);

  const { isOpen: isEditOpen, onToggle: onEditOpenToggle } = useDisclosure();
  const { isOpen: isDeleteOpen, onToggle: onDeleteOpenToggle } =
    useDisclosure();

  const { mutateAsync: deleteTimestamp } =
    WorkingInfoApi.hooks.useDeleteTimestamp(employee);

  const { mutateAsync: editTimestamp } =
    WorkingInfoApi.hooks.useEditTimestamp(employee);

  function test(this: yup.TestContext<Record<string, unknown>>): boolean {
    const values = this.parent as Record<string, string>;
    const [startHour, startMin] = values?.start?.split(':') ?? [null, null];
    const [endHour, endMin] = values?.end?.split(':') ?? [null, null];
    const editedStart = dateFns.set(new Date(ts.start), {
      minutes: parseInt(startMin!),
      hours: parseInt(startHour!),
    });
    const editedEnd = dateFns.set(new Date(ts.end as number), {
      minutes: parseInt(endMin!),
      hours: parseInt(endHour!),
    });
    return dateFns.isBefore(editedStart, editedEnd);
  }

  return (
    <>
      <Grid templateColumns="repeat(5, 1fr)" gap={1} cursor="default">
        <Text fontWeight="semibold">
          {ts.type === StampType.WORK
            ? 'working.history.work'
            : 'working.history.pause'}
        </Text>
        <LabeledText label="working.history.bone.start" mt={0}>
          {dateFns.format(new Date(ts.start), 'HH:mm')}
        </LabeledText>
        {!ts.end || (
          <>
            <LabeledText label="working.history.bone.end" mt={0}>
              {dateFns.format(new Date(ts.end), 'HH:mm')}
            </LabeledText>
            <Box mt={-0.5}>
              <Tooltip
                label={
                  disabled
                    ? t('working.history.bone.edit.disabled')
                    : t('working.history.bone.ts.edit')
                }
              >
                <EditIcon
                  cursor="pointer"
                  onClick={() => !disabled && onEditOpenToggle()}
                  _hover={{
                    boxShadow: 'lg',
                    color,
                  }}
                  mr={2}
                  color={color}
                />
              </Tooltip>
              <Tooltip
                label={
                  disabled
                    ? t('working.history.bone.edit.disabled')
                    : t('working.history.bone.ts.delete')
                }
              >
                <DeleteIcon
                  cursor="pointer"
                  onClick={() => !disabled && onDeleteOpenToggle()}
                  _hover={{
                    boxShadow: 'lg',
                    color,
                  }}
                  color={color}
                />
              </Tooltip>
            </Box>
          </>
        )}
      </Grid>

      <DefaultModalDialog
        dialogHeaderText="editTimestamp.header"
        isOpen={isEditOpen}
        onSubmit={(values: { start: string; end: string; type: StampType }) =>
          editTimestamp({ date, ts, ...values }).then(onEditOpenToggle)
        }
        showCancelButton
        submitText="editTimestamp.submit"
        onCancel={onEditOpenToggle}
        initialValues={{
          start: dateFns.format(new Date(ts.start), 'HH:mm'),
          end: dateFns.format(new Date(ts.end as number), 'HH:mm'),
          type: ts.type,
        }}
        validateOnChange
        validationSchema={yup.object().shape({
          start: yup.string().required().test({
            message: 'deleteTimestamp.startbeforeend',
            test,
          }),
          end: yup.string().required(),
          type: yup
            .mixed<StampType>()
            .oneOf(Object.values(StampType))
            .required(),
        })}
      >
        <Flex direction="row">
          <Box w="full" pr={1}>
            <InputField
              label="createTimestamp.start"
              name="start"
              type="time"
            />
          </Box>
          <Box w="full" pl={1} pr={1}>
            <InputField label="createTimestamp.end" name="end" type="time" />
          </Box>
          <Box pl={1}>
            <SelectField
              name="type"
              fields={[StampType.WORK, StampType.PAUSE]}
              labels={(field) => {
                switch (field) {
                  case StampType.WORK:
                    return 'createTimestamp.work';
                  case StampType.PAUSE:
                    return 'createTimestamp.pause';
                  default:
                    return 'createTimestamp.initial';
                }
              }}
              values={(field) => field}
              keys={(field) => field}
              label="createTimestamp.type"
            />
          </Box>
        </Flex>
      </DefaultModalDialog>
      <DefaultAlertDialog
        dialogHeaderText="deleteTimestamp.header"
        dialogBodyText="deleteTimestamp.body"
        bodyParams={[
          dateFns.format(new Date(ts.start), 'HH:mm'),
          dateFns.format(new Date(ts.end as number), 'HH:mm'),
          formatName(employee),
        ]}
        isOpen={isDeleteOpen}
        onConfirmedAction={() => void deleteTimestamp(ts)}
        onCancel={onDeleteOpenToggle}
      />
    </>
  );
};
